import { Component, inject } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IIcon } from '../../../dashboards/interfaces/tiles/definition-helpers/icon.interface';
import { IPresentationTileInstance } from '../../../dashboards/interfaces/tiles/definitions/presentation-tile-definition.interface';
import { ICON_STYLES_OPTIONS } from '../../constants/icon-styles.constant';
import { ISelectOption } from '../../interfaces/select-option.interface';
import { TileCreatorHelpersService } from '../../services/tile-creator-helpers.service';
import { TileCreatorService } from '../../services/tile-creator.service';
import { TILE_REQUEST } from '../../tokens/tile-request.token';
import { TileSetupWizardComponent } from '../tile-setup-wizard.component';

const IntegerValidator: ValidatorFn = (control) => {
  return Number.isInteger(control.value) ? null : { notInteger: true };
};

class FontSizeFormControl extends FormControl<number> {
  constructor(defaultValue?: number) {
    super(defaultValue, [Validators.min(6), Validators.max(64), IntegerValidator]);
  }
}

@Component({
  standalone: false,
  selector: 'lib-presentation-setup-wizard',
  templateUrl: './presentation-setup-wizard.component.html',
  styleUrls: ['../shared-tile-creator-styles.scss', './presentation-setup-wizard.component.scss'],
})
export class PresentationSetupWizardComponent extends TileSetupWizardComponent<IPresentationTileInstance> {
  readonly setupForm = new FormGroup({
    text: new FormControl(),
    background: new FormControl(),
    textFontSize: new FontSizeFormControl(6),
    height: new FormControl(),
    textFontColor: new FormControl('#000000'),

    // Buttons
    linkButtons: new FormArray<FormGroup>([]),

    // Icons
    icons: new FormArray<FormGroup>([]),
  });

  iconStylesOptions: ISelectOption[];
  fontSizesOptions: string[];

  constructor(
    private readonly fb: FormBuilder,
    private readonly tileCreatorHelpersService: TileCreatorHelpersService,
    private readonly tileCreatorService: TileCreatorService,
  ) {
    super();

    this.tileInstance = inject(TILE_REQUEST).tileInstance;
  }

  public ngOnInit(): void {
    this.initSelectOptions();
    this.initSetupForms();

    this.hookForm();
  }

  initSelectOptions() {
    this.iconStylesOptions = ICON_STYLES_OPTIONS;
    this.fontSizesOptions = this.tileCreatorHelpersService.generateFontSize();
  }

  initSetupForms() {
    if (this.tileInstance) {
      this.setupForm.patchValue(this.tileInstance);

      this.tileInstance.icons?.forEach((icon: IIcon) => {
        this.addIcon(icon);
      });

      //TODO : Replace this workaround - ideally handle this in editor widgets
      for (const field of ['textFontSize']) {
        if (this.tileInstance[field]) {
          let originalValue = this.tileInstance[field];
          if (originalValue) {
            let value = originalValue;
            if (!isNaN(originalValue)) {
              let valueStr = originalValue.slice(0, -2);
              value = parseInt(valueStr);
            }
            this.setupForm.controls[field].setValue(value);
          }
        }
      }
    }
  }

  addIcon(icon?: IIcon) {
    const iconFormGroup = this.fb.group({
      icon: [''],
      iconStyle: ['basic'],
      iconColor: ['#000000'],
      iconOutlineColor: ['#000000'],
      iconBackgroundColor: ['#000000'],
    });

    iconFormGroup.patchValue(icon);

    (this.setupForm.controls.icons as FormArray).push(iconFormGroup);
  }

  removeIcon(iconIndex: number) {
    (this.setupForm.controls.icons as FormArray).removeAt(iconIndex);
  }

  onCreate() {
    Object.assign(this.tileInstance, this.getChanges());
    this.tileCreatorService.onChange.next();
  }

  getChanges() {
    return {
      tileType: 'presentation',
      canView: 'true',
      ...this.setupForm.value,
    };
  }
}
